/**
 * LandingPageSkeleton.tsx
 * 
 * Comprehensive skeleton loader for the main landing/index page.
 * Provides visual feedback during initial load and data fetching.
 * Follows mobile-first design principles with responsive behavior.
 * Implements staggered animation for more engaging visual feedback.
 */

import React from 'react';
import { Skeleton } from '../skeleton';
import { cn } from '@/lib/utils';

/**
 * StaggeredSkeletonItem - Component for creating staggered loading animation effects
 * Each skeleton item has a slightly delayed animation based on its index
 */
function StaggeredSkeletonItem({ 
  className,
  index = 0,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { index?: number }) {
  // Calculate delay based on item index (100ms increments up to 500ms max)
  const animationDelay = `${Math.min(index * 100, 500)}ms`;
  
  return (
    <Skeleton 
      className={cn(className)} 
      style={{ 
        animationDelay,
        opacity: 0,
        animation: `skeleton-fade-in 0.5s ease forwards ${animationDelay}, 
                    skeleton-pulse 2s ease-in-out infinite ${animationDelay}`
      }}
      {...props}
    />
  );
}

export function LandingPageSkeleton() {
  return (
    <div className="w-full" aria-hidden="true" role="status">
      {/* Hero Section Skeleton */}
      <div className="w-full max-w-7xl mx-auto px-4 py-16">
        <div className="flex flex-col md:flex-row items-center gap-8">
          <div className="w-full md:w-1/2 space-y-4">
            <StaggeredSkeletonItem className="h-12 w-3/4" index={0} />
            <StaggeredSkeletonItem className="h-6 w-full" index={1} />
            <StaggeredSkeletonItem className="h-6 w-5/6" index={2} />
            <StaggeredSkeletonItem className="h-6 w-4/5" index={3} />
            <div className="pt-4 flex gap-4">
              <StaggeredSkeletonItem className="h-10 w-32 rounded-md" index={4} />
              <StaggeredSkeletonItem className="h-10 w-32 rounded-md" index={5} />
            </div>
          </div>
          <div className="w-full md:w-1/2">
            <StaggeredSkeletonItem className="h-80 w-full rounded-xl" index={6} />
          </div>
        </div>
      </div>

      {/* Features Section Skeleton */}
      <div className="bg-muted/10 w-full py-16">
        <div className="max-w-7xl mx-auto px-4">
          <StaggeredSkeletonItem className="h-10 w-64 mx-auto mb-12" index={7} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[0, 1, 2].map((i) => (
              <div key={i} className="space-y-4 p-6">
                <StaggeredSkeletonItem className="h-12 w-12 rounded-full" index={8 + i} />
                <StaggeredSkeletonItem className="h-6 w-3/4" index={11 + i} />
                <StaggeredSkeletonItem className="h-4 w-full" index={14 + i} />
                <StaggeredSkeletonItem className="h-4 w-5/6" index={17 + i} />
                <StaggeredSkeletonItem className="h-4 w-4/5" index={20 + i} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* How It Works Section Skeleton */}
      <div className="w-full py-16">
        <div className="max-w-7xl mx-auto px-4">
          <StaggeredSkeletonItem className="h-10 w-64 mx-auto mb-12" index={23} />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="space-y-3">
                <StaggeredSkeletonItem className="h-8 w-8 rounded-full mx-auto" index={24 + i} />
                <StaggeredSkeletonItem className="h-5 w-32 mx-auto" index={28 + i} />
                <StaggeredSkeletonItem className="h-24 w-full rounded-lg" index={32 + i} />
                <StaggeredSkeletonItem className="h-4 w-full" index={36 + i} />
                <StaggeredSkeletonItem className="h-4 w-5/6 mx-auto" index={40 + i} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ Section Skeleton */}
      <div className="bg-muted/10 w-full py-16">
        <div className="max-w-3xl mx-auto px-4">
          <StaggeredSkeletonItem className="h-10 w-64 mx-auto mb-8" index={44} />
          {[0, 1, 2].map((i) => (
            <div key={i} className="mb-4 border rounded-lg p-4">
              <StaggeredSkeletonItem className="h-6 w-5/6" index={45 + i} />
              <StaggeredSkeletonItem className="h-4 w-full mt-2" index={48 + i} />
              <StaggeredSkeletonItem className="h-4 w-5/6 mt-1" index={51 + i} />
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section Skeleton */}
      <div className="w-full py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <StaggeredSkeletonItem className="h-10 w-3/4 mx-auto mb-4" index={54} />
          <StaggeredSkeletonItem className="h-5 w-full max-w-lg mx-auto mb-8" index={55} />
          <StaggeredSkeletonItem className="h-12 w-40 mx-auto rounded-md" index={56} />
        </div>
      </div>
    </div>
  );
}

export default LandingPageSkeleton;
